import * as yup from 'yup';

/**
 * A regra do campo Nome de todos os forms de cadastro (SCRUM-2133).
 *
 * Um nome é uma sequência de palavras. Cada palavra começa por letra de qualquer escrita (`\p{L}`) e
 * segue com letras e as marcas combinantes que algumas escritas precisam (`\p{M}`: Devanagari, Thai,
 * vietnamita decomposto). ZWNJ/ZWJ (persa, indianas) só valem entre duas letras. Entre as palavras vai
 * um separador: espaço (inclusive o U+3000 do IME japonês); um hífen, travessão, apóstrofo reto ou curvo
 * (o iOS troca um pelo outro), o `·` do catalão, o `・` do katakana ou o `‧` de Taiwan, com ou sem
 * espaço em volta; ou um ponto seguido de espaço (`J. R. R. Tolkien`). O nome pode terminar em ponto
 * (`King Jr.`).
 *
 * Como o separador precisa de uma palavra depois, pontuação seguida (`Ana....`, `A - - -`) não passa. E
 * como o ponto precisa de espaço depois, `noping.com` e `bit.ly` também não: o nome é repetido em e-mail
 * transacional e no convite de squad, e um domínio ali vira link. Dígitos, símbolos e emoji ficam de fora.
 */
// ZWNJ/ZWJ ficam fora da classe: dentro dela o ESLint (no-misleading-character-class) lê o ZWJ como
// emenda de sequência, e aqui ele é só um caractere permitido, não junta nada à classe.
const WORD = String.raw`\p{L}[\p{L}\p{M}]*(?:(?:\u200C|\u200D)[\p{L}\p{M}]+)*`;
const SEPARATOR = String.raw`(?:\p{Zs}*['’\-\u2010\u2013·・\u2027]\p{Zs}*|\.\p{Zs}+|\p{Zs}+)`;
export const PERSON_NAME_PATTERN = new RegExp(`^${WORD}(?:${SEPARATOR}${WORD})*\\.?$`, 'u');

// `usuario.nome` na API é utf8mb3 e a conexão é utf8mb4: um caractere fora do plano básico (CJK Ext-B
// como 𠮷, emoji) faz o INSERT falhar (400 genérico no cadastro, no trial e na TIM; 500 no convite de
// squad). A API (SCRUM-2133) passou a remover esses caracteres antes de gravar, mas barrar aqui dá ao
// usuário uma mensagem em vez de um nome alterado sem aviso. Também barra as letras "estilizadas" do
// plano 1 (𝐀𝐝𝐦𝐢𝐧), que são `\p{L}` e a API reduziria a um nome vazio.
// ZWNJ/ZWJ só existem para escritas que ligam letras (persa, indianas). Encostado numa letra latina ele não
// muda nada na tela e só serve para gravar `Ad<ZWJ>min` diferente de `Admin`; a API (SCRUM-2133) remove
// esse caso, e aqui ele é recusado para o front e a API concordarem.
const LATIN_JOINER_PATTERN = /\p{Script=Latin}(?:\u200C|\u200D)|(?:\u200C|\u200D)\p{Script=Latin}/u;

const SUPPLEMENTARY_PLANE_PATTERN = /[\u{10000}-\u{10FFFF}]/u;

// Invisíveis que o copiar-colar traz no meio do nome (ZWSP, soft hyphen, word joiner, BOM). A API já os
// remove; tirar aqui evita recusar um nome que o usuário não tem como ver o que está errado.
const INVISIBLE_CHARACTERS_PATTERN = /[\u00AD\u200B\u2060\uFEFF]/gu;

// Katakana de meia largura (teclado japonês antigo, ｶﾀｶﾅ): o NFKC só nesse trecho o leva à forma normal
// (カタカナ, e o `･` vira `・`). No nome inteiro o NFKC mudaria o que o usuário digitou (ligaduras, `²`).
const HALFWIDTH_KATAKANA_PATTERN = /[\uFF61-\uFF9F]+/gu;

const PERSON_NAME_MAX_LENGTH = 255;

/** NFC + invisíveis removidos + katakana de meia largura convertido + espaços colapsados + trim: a forma que vai para a API. */
export function normalizePersonName(value: string): string {
  return value
    .normalize('NFC')
    .replace(INVISIBLE_CHARACTERS_PATTERN, '')
    .replace(HALFWIDTH_KATAKANA_PATTERN, (katakana) => katakana.normalize('NFKC'))
    .replace(/\s+/gu, ' ')
    .trim();
}

export function isValidPersonName(value: string): boolean {
  return PERSON_NAME_PATTERN.test(value) && !LATIN_JOINER_PATTERN.test(value) && !SUPPLEMENTARY_PLANE_PATTERN.test(value);
}

/**
 * O schema yup do campo Nome. O transform normaliza antes de validar, então um nome só de espaços cai
 * no `required` (e não no `invalid`), e o `data` do `handleSubmit` já chega normalizado.
 */
export function personNameSchema(requiredMessage: string, invalidMessage: string) {
  return yup
    .string()
    .transform((value: unknown) => (typeof value === 'string' ? normalizePersonName(value) : value))
    .required(requiredMessage)
    .test('person-name', invalidMessage, (value) => !value || isValidPersonName(value))
    .max(PERSON_NAME_MAX_LENGTH);
}
